import { useLocation } from '@reach/router'
import { graphql, useStaticQuery } from 'gatsby'
import { sha256 } from 'js-sha256'
import React from 'react'
import Helmet from 'react-helmet'

import { Language } from '../hooks/useLanguage'
import { useSiteMetadata } from '../hooks/useSiteMetadata'

declare let analytics: any

const defaultImageSrc = 'meta-default.png'
const twitterAuthor = '@airfocus'

interface Props {
  title: string
  description?: string
  imageSrc?: string
  lang?: Language
  link?: React.JSX.IntrinsicElements['link'][]
  meta?: { name: string; content: string; property?: any }[]
  keywords?: string[]
  withTitleTemplate?: boolean // adds the appended "| airfocus" to link/titles
  noindex?: boolean
}

const SEO: React.FunctionComponent<Props> = ({
  title,
  description,
  imageSrc,
  lang,
  link,
  meta = [],
  keywords = [],
  withTitleTemplate = true,
  noindex = false,
}) => {
  const siteMetadata = useSiteMetadata()
  const data = useStaticQuery(graphql`
    {
      images: allFile(filter: { relativePath: { regex: "/^meta//" } }) {
        edges {
          node {
            relativePath
            name
            publicURL
          }
        }
      }
    }
  `)
  const image = data.images.edges.find((n: any) => n.node.relativePath === `meta/${imageSrc || defaultImageSrc}`)
  const fullImageSrc =
    imageSrc && imageSrc.match(/^https?:/) ? imageSrc : image ? siteMetadata.siteUrl + image.node.publicURL : undefined
  useSegmentPageTracking(title)
  useSegmentConvertFlowTracking()

  return (
    <Helmet
      htmlAttributes={{
        lang,
      }}
      link={link}
      title={title}
      titleTemplate={withTitleTemplate ? `%s | ${siteMetadata.title}` : `%s`}
      meta={[
        {
          name: `description`,
          content: description,
        },
        {
          property: `og:title`,
          content: title,
        },
        {
          property: `og:description`,
          content: description,
        },
        {
          property: `og:image`,
          content: fullImageSrc,
        },
        {
          property: `og:type`,
          content: `website`,
        },
        {
          name: `twitter:card`,
          content: `summary_large_image`,
        },
        {
          name: `twitter:creator`,
          content: twitterAuthor,
        },
        {
          name: `twitter:title`,
          content: title,
        },
        {
          name: `twitter:description`,
          content: description,
        },
        {
          property: `twitter:image`,
          content: fullImageSrc,
        },
        noindex
          ? {
              name: `robots`,
              content: `noindex`,
            }
          : {},
      ]
        .concat(
          keywords.length > 0
            ? {
                name: `keywords`,
                content: keywords.join(`, `),
              }
            : []
        )
        .concat(meta)
        .filter((prop) => !!prop && !!prop.content)}
    />
  )
}

export default SEO

function useSegmentPageTracking(title: string) {
  const location = useLocation()
  React.useEffect(() => {
    try {
      if (typeof analytics !== 'undefined') {
        analytics.page({
          title,
          path: location.pathname,
          search: location.search,
          url: location.href,
          referrer: document.referrer,
        })
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err)
    }
    const timeoutId = window.setTimeout(() => {
      try {
        if (typeof analytics !== 'undefined') {
          analytics.track('SoftConversion_Website_Stayed20SecOnPage', {
            category: 'SoftConversion',
            action: 'SoftConversion_Website_Stayed20SecOnPage',
            label: location.pathname,
            path: location.pathname,
            search: location.search,
            url: location.href,
            referrer: document.referrer,
          })
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(err)
      }
    }, 20000)
    return () => window.clearTimeout(timeoutId)
  }, [title, location.pathname, location.search, location.href])
}

function useSegmentConvertFlowTracking() {
  React.useEffect(() => {
    const handler = (event: any) => {
      try {
        if (typeof analytics !== 'undefined') {
          analytics.track('Lead_Website_DownloadedContent', {
            category: 'Lead',
            action: 'Lead_Website_DownloadedContent',
            label: event.detail.cta.name,
          })
          const analyticsHash = sha256(event.detail.fields.email)
          analytics.track('Lead_Website_AnalyticsId', {
            category: 'AnalyticsId',
            action: 'Lead_Website_DownloadedContent',
            label: analyticsHash,
          })
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(err)
      }
    }
    window.addEventListener('cfSubmit', handler)
    return () => window.removeEventListener('cfSubmit', handler)
  }, [])
}
